import { useState, useCallback, useEffect } from "react";
import { memoryStore, setItemImmediate } from "../utils/storage";

const PLANS_KEY = "pension_plans_v1";

function loadAll() {
  try {
    const raw = memoryStore.getItem(PLANS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

// Plans are critical user data — push to cloud immediately (no debounce)
// so a hard-refresh right after save can't lose the data.
function saveAll(all) {
  try { setItemImmediate(PLANS_KEY, JSON.stringify(all)); } catch {}
}

export function usePlanStore(clientId) {
  const [plans, setPlans] = useState(() => loadAll()[clientId] ?? []);

  useEffect(() => {
    const onSync = () => setPlans(loadAll()[clientId] ?? []);
    window.addEventListener('pension-data-synced', onSync);
    return () => window.removeEventListener('pension-data-synced', onSync);
  }, [clientId]);

  const persist = useCallback((newPlans) => {
    const all = loadAll();
    all[clientId] = newPlans;
    saveAll(all);
  }, [clientId]);

  const savePlan = useCallback((name, data) => {
    const plan = { id: `plan-${Date.now()}`, name, createdAt: new Date().toISOString(), data };
    let newId = plan.id;
    setPlans(prev => {
      const next = [...prev, plan];
      persist(next);
      return next;
    });
    return newId;
  }, [persist]);

  const updatePlan = useCallback((planId, data, newName) => {
    setPlans(prev => {
      const next = prev.map(p => p.id === planId
        ? { ...p, data, updatedAt: new Date().toISOString(), ...(newName ? { name: newName } : {}) }
        : p);
      persist(next);
      return next;
    });
  }, [persist]);

  const deletePlan = useCallback((planId) => {
    setPlans(prev => {
      const next = prev.filter(p => p.id !== planId);
      persist(next);
      return next;
    });
  }, [persist]);

  const renamePlan = useCallback((planId, name) => {
    setPlans(prev => {
      const next = prev.map(p => p.id === planId ? { ...p, name } : p);
      persist(next);
      return next;
    });
  }, [persist]);

  return { plans, savePlan, updatePlan, deletePlan, renamePlan };
}
